import { XMarkIcon } from '@heroicons/react/24/outline'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react'
import { Controller, useForm } from 'react-hook-form'

import {
  joinAutoNumber,
  useAutos,
  useCreateAuto,
  useDeleteAuto,
  useUpdateAuto
} from '@/api/models/parking'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { cn } from '@/app/utils'
import { AutoNumberInput } from '@/components/entities/passes/ui/PassForms/FormBlock/AutoMasked/AutoNumberInput'
import { ConfirmDeleteModal } from '@/components/entities/passes/ui/PassForms/FormBlock/ConfirmDeleteModal'
import {
  DEFAULT_AUTO_MODEL_MAX_LENGTH,
  DEFAULT_AUTO_MODEL_MAX_LENGTH_MESSAGE_FROM
} from '@/components/entities/requests/model/constants'
import {
  AutoFormData,
  autoFormSchema,
  DEFAULT_AUTO_EMOJI
} from '@/components/entities/user/ui/UserAuto/model/schema'
import {
  hasAutoFormServerErrors,
  parseAutoServerErrorField
} from '@/components/entities/user/ui/UserAuto/model/serverErrors'
import { AppButton } from '@/components/shared/button'
import { EmojiPicker } from '@/components/shared/emoji-picker'
import { AppInput } from '@/components/shared/input'
import AppInputMessage from '@/components/shared/input/ui/AppInputMessage'
import { AppLoader } from '@/components/shared/loader'
import { AppTextareaAutoSize } from '@/components/shared/textarea'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_DESCRIPTION_LENGTH_MESSAGE_FROM
} from '@/utils/constants'

import { SetUserProfileRoute } from '../../../hooks/useUserProfileRoute'
import { UserProfileScreens } from '../../../lib/types'
import { useUserProfileStore } from '../../../model/store'
import { FixedBottomDrawerButton } from '../../FixedBottomDrawerButton'
import UserAutoError from './UserAutoError'

const SAVE_ERROR_TITLE = 'Сохранить ТС не удалось'
const DELETE_ERROR_TITLE = 'Удалить ТС не удалось'

type AutoPendingAction = 'save' | 'delete' | null

type Props = {
  autoId?: string
  setUserProfileRoute: SetUserProfileRoute
  onSavedToastMessage: Dispatch<SetStateAction<string | null>>
}

const emptyValues: AutoFormData = {
  number: '',
  isStandard: true,
  model: '',
  description: '',
  emoji: DEFAULT_AUTO_EMOJI
}

const UserAutoScreen: FC<Props> = ({
  autoId,
  setUserProfileRoute,
  onSavedToastMessage
}) => {
  const { data: autos } = useAutos()
  const [isConfirmDeleteOpened, setIsConfirmDeleteOpened] = useState(false)
  const [pendingAction, setPendingAction] = useState<AutoPendingAction>(null)
  const { setIsError, setErrorTitle } = useUserProfileStore()
  const { reachGoal } = useYandexMetrika()

  const auto = autos.find(({ id }) => id === autoId)
  const isEditing = Boolean(autoId)

  const {
    mutate: createAuto,
    error: createAutoError,
    reset: resetCreateAuto
  } = useCreateAuto()
  const {
    mutate: updateAuto,
    error: updateAutoError,
    reset: resetUpdateAuto
  } = useUpdateAuto()
  const {
    mutate: deleteAuto,
    error: deleteAutoError,
    reset: resetDeleteAuto
  } = useDeleteAuto()

  const isSaving = pendingAction === 'save'
  const isDeleting = pendingAction === 'delete'
  const saveAutoError = createAutoError ?? updateAutoError
  const autoMutationError = saveAutoError ?? deleteAutoError
  const shouldShowErrorPlug =
    Boolean(autoMutationError) && !hasAutoFormServerErrors(saveAutoError)
  const errorTitle = deleteAutoError ? DELETE_ERROR_TITLE : SAVE_ERROR_TITLE

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    reset,
    setError,
    formState: { errors }
  } = useForm<AutoFormData>({
    mode: 'onSubmit',
    resolver: yupResolver(autoFormSchema),
    defaultValues: emptyValues
  })

  useEffect(() => {
    if (!auto) {
      return
    }

    reset({
      number: joinAutoNumber(auto.number, auto.region),
      isStandard: auto.isStandard,
      model: auto.model ?? '',
      description: auto.description,
      emoji: auto.emoji || DEFAULT_AUTO_EMOJI
    })
  }, [auto, reset])

  const applyServerErrors = useCallback(() => {
    const serverErrors = saveAutoError?.response?.data?.errors

    if (!serverErrors?.length) {
      return
    }

    serverErrors.forEach(({ field, message }) => {
      const formField = parseAutoServerErrorField(field)

      if (formField && message) {
        setError(formField, { type: 'server', message })
      }
    })
  }, [saveAutoError, setError])

  useEffect(() => {
    applyServerErrors()
  }, [applyServerErrors])

  useEffect(() => {
    setIsError(shouldShowErrorPlug)
    setErrorTitle(shouldShowErrorPlug ? errorTitle : null)
  }, [shouldShowErrorPlug, errorTitle, setIsError, setErrorTitle])

  useEffect(
    () => () => {
      setIsError(false)
      setErrorTitle(null)
    },
    [setIsError, setErrorTitle]
  )

  const goToList = () => setUserProfileRoute(UserProfileScreens.Autos)

  const stopPendingAction = () => setPendingAction(null)

  const handleErrorPlugClose = () => {
    setPendingAction(null)
    setIsConfirmDeleteOpened(false)
    resetCreateAuto()
    resetUpdateAuto()
    resetDeleteAuto()
  }

  const onSubmit = (formData: AutoFormData) => {
    if (isSaving) {
      return
    }

    const payload = {
      number: formData.number,
      isStandard: formData.isStandard,
      model: formData.model || null,
      description: formData.description,
      emoji: formData.emoji
    }

    setPendingAction('save')

    if (autoId) {
      updateAuto(
        { id: autoId, data: payload },
        {
          onSuccess: () => {
            reachGoal(YandexMetrikaEvent.MY_AUTO_EDIT)
            onSavedToastMessage('Транспортное средство сохранено')
            goToList()
          },
          onError: stopPendingAction
        }
      )
      return
    }

    createAuto(payload, {
      onSuccess: () => {
        reachGoal(YandexMetrikaEvent.MY_AUTO_ADD)
        onSavedToastMessage('Транспортное средство сохранено')
        goToList()
      },
      onError: stopPendingAction
    })
  }

  const onDeleteConfirm = () => {
    if (!autoId || isDeleting) {
      return
    }

    setPendingAction('delete')

    deleteAuto(autoId, {
      onSuccess: () => {
        reachGoal(YandexMetrikaEvent.MY_AUTO_DELETE)
        onSavedToastMessage('Транспортное средство удалено')
        goToList()
      },
      onError: stopPendingAction
    })
  }

  if (shouldShowErrorPlug) {
    return (
      <>
        <UserAutoError />

        <FixedBottomDrawerButton
          text="Понятно"
          onClick={handleErrorPlugClose}
        />
      </>
    )
  }

  return (
    <>
      <form
        id="user-auto-form"
        className="mb-[120px] px-40 pb-40 pt-16 lg:px-24 lg:pb-24 lg:pt-0 sm:px-16 sm:pb-20"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex items-start gap-8 sm:flex-wrap sm:gap-12">
          <Controller
            name="number"
            control={control}
            render={({ field }) => (
              <AutoNumberInput
                value={field.value}
                isStandard={getValues('isStandard')}
                className="min-w-0 flex-[11] sm:basis-full"
                boxClassName="md:h-56"
                invalid={Boolean(errors.number)}
                invalidMessage={errors.number?.message}
                onStandardChange={isStandard =>
                  setValue('isStandard', isStandard, { shouldDirty: true })
                }
                onChange={(value, skipValidation) => {
                  clearErrors('number')

                  if (skipValidation) {
                    setValue('number', value, { shouldValidate: false })
                    return
                  }

                  field.onChange(value)
                }}
              />
            )}
          />

          <Controller
            name="model"
            control={control}
            render={({ field }) => (
              <AppInput
                {...field}
                placeholder="Марка"
                className="md:h-56 sm:h-48"
                inputClassName={cn(
                  'w-full min-w-0 truncate',
                  field.value && 'pr-44'
                )}
                rootClassName="min-w-0 flex-[10]"
                maxLength={DEFAULT_AUTO_MODEL_MAX_LENGTH}
                maxLengthMessageFrom={
                  DEFAULT_AUTO_MODEL_MAX_LENGTH_MESSAGE_FROM
                }
                invalid={Boolean(errors.model)}
                invalidMessage={errors.model?.message}
                extraContent={
                  field.value ? (
                    <button
                      type="button"
                      aria-label="Очистить марку"
                      className="absolute right-12 top-1/2 -translate-y-1/2"
                      onClick={() => field.onChange('')}
                    >
                      <XMarkIcon className="h-20 w-20 text-grey-50" />
                    </button>
                  ) : null
                }
              />
            )}
          />

          <Controller
            name="emoji"
            control={control}
            render={({ field }) => (
              <EmojiPicker
                className="shrink-0"
                value={field.value}
                dataTestId="auto-emoji-picker"
                onChange={field.onChange}
              />
            )}
          />
        </div>

        <AppInputMessage message={errors.emoji?.message} />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <AppTextareaAutoSize
              {...field}
              placeholder="Описание"
              className="mt-20 sm:mt-8"
              maxLength={MAX_DESCRIPTION_LENGTH}
              maxLengthMessageFrom={MAX_DESCRIPTION_LENGTH_MESSAGE_FROM}
              invalid={Boolean(errors.description)}
            />
          )}
        />

        <AppInputMessage message={errors.description?.message} />
      </form>

      <FixedBottomDrawerButton>
        {isEditing && (
          <AppButton
            fluid
            variant="backless"
            size="large"
            className="!text-red-70 sm:!h-44"
            onClick={() => setIsConfirmDeleteOpened(true)}
          >
            Удалить
          </AppButton>
        )}

        <AppButton
          fluid
          form="user-auto-form"
          variant="secondary"
          size="large"
          type="submit"
          aria-busy={isSaving}
          className={cn('sm:!h-44', isSaving && 'pointer-events-none')}
        >
          {isSaving ? <AppLoader noText variant="primary" /> : 'Сохранить'}
        </AppButton>
      </FixedBottomDrawerButton>

      <ConfirmDeleteModal
        opened={isConfirmDeleteOpened}
        header="Удалить транспортное средство?"
        isPending={isDeleting}
        onClose={() => setIsConfirmDeleteOpened(false)}
        onConfirm={onDeleteConfirm}
      />
    </>
  )
}

export { UserAutoScreen }
